import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Stack
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { formatMonthDisplay, getOutcomeTimelineDisplay, getActualDateDisplay } from '../../../utils/wizardHelpers';

function RoleTimelineModal({ 
  open, 
  onClose, 
  role, 
  projectMonths, 
  onSave, 
  readOnly = false 
}) {
  const [allocation, setAllocation] = useState(100);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(true);
  
  // Get milestone context from role
  const milestoneContext = role?.milestoneContext;
  const projectStartMonth = role?.milestoneContext?.projectStartMonth || '2026-01';

              // Initialize modal state when opened - supports both calendar and project-relative formats
  useEffect(() => {
    if (open && role && milestoneContext) {
      // For allocation, prioritize role's current allocation, but default to 100%
      const initialAllocation = role.allocation || 100;
      setAllocation(initialAllocation);
      
      // Check if role has custom timeline that differs from milestone
      if (role.dateRanges && role.dateRanges.length > 0 && role.useDefaultTimeline === false) {
        // Role has custom timeline - detect format and convert to dates
        const firstRange = role.dateRanges[0];
        let customStartDate, customEndDate;
        
        // Detect format based on data characteristics and version markers
        const isCalendarFormat = (
          firstRange.timelineVersion === '3.0' || // Explicit new format marker
          !projectStartMonth || // No project context available
          (firstRange.startMonth >= 1 && firstRange.startMonth <= 12 && 
           firstRange.endMonth >= 1 && firstRange.endMonth <= 12 &&
           firstRange.startMonth <= firstRange.endMonth && 
           !firstRange.useProjectRelative) // Reasonable calendar months without project-relative flag
        );
        
        if (isCalendarFormat) {
          // New format: Direct calendar months (1-12)
          const startMonth = Math.max(1, Math.min(12, firstRange.startMonth || 1));
          const endMonth = Math.max(startMonth, Math.min(12, firstRange.endMonth || startMonth));
          const currentYear = new Date().getFullYear();
          customStartDate = dayjs().year(currentYear).month(startMonth - 1).startOf('month');
          customEndDate = dayjs().year(currentYear).month(endMonth - 1).startOf('month');
        } else {
          // Legacy format: Project-relative months - convert to calendar dates
          const projectStart = dayjs(projectStartMonth + '-01');
          customStartDate = projectStart.add((firstRange.startMonth || milestoneContext.milestoneStartMonth || 1) - 1, 'month');
          customEndDate = projectStart.add((firstRange.endMonth || milestoneContext.milestoneEndMonth || projectMonths) - 1, 'month');
        }
        
        setStartDate(customStartDate);
        setEndDate(customEndDate);
        
        console.log('RoleTimelineModal: Using custom timeline:', {
          roleName: role.name,
          format: isCalendarFormat ? 'calendar' : 'project-relative',
          customStart: customStartDate.format('YYYY-MM-DD'),
          customEnd: customEndDate.format('YYYY-MM-DD'),
          allocation: initialAllocation
        });
      } else {
        // Default to milestone dates - always use calendar format for new assignments
        const milestoneStart = dayjs(milestoneContext.milestoneStartDate || `${new Date().getFullYear()}-01-01`);
        const milestoneEnd = dayjs(milestoneContext.milestoneEndDate || `${new Date().getFullYear()}-12-31`);
        setStartDate(milestoneStart);
        setEndDate(milestoneEnd);
        
        console.log('RoleTimelineModal: Using milestone timeline (default):', {
          roleName: role.name,
          milestoneStart: milestoneStart.format('YYYY-MM-DD'),
          milestoneEnd: milestoneEnd.format('YYYY-MM-DD'),
          allocation: initialAllocation
        });
      }
      
      setErrors({});
      setIsValid(true);
    }
  }, [open, role, milestoneContext, projectStartMonth, projectMonths]);

      const validateDates = () => {
    const errors = {};
    
    if (!startDate || !endDate) {
      errors.dates = 'Both start and end dates are required';
    } else if (startDate.isAfter(endDate)) {
      errors.dates = 'Start date must be before or equal to end date';
    }
    
    if (allocation < 0 || allocation > 100) {
      errors.allocation = 'Allocation must be between 0% and 100%';
    }
    
    return errors;
  };

  // Real-time validation effect
  useEffect(() => {
    const validationErrors = validateDates();
    setErrors(validationErrors);
    setIsValid(Object.keys(validationErrors).length === 0);
  }, [startDate, endDate, allocation]);

      const handleAllocationChange = (newValue) => {
    const validValue = Math.max(0, Math.min(100, Number(newValue) || 0));
    setAllocation(validValue);
    setErrors(prev => ({ ...prev, allocation: null }));
  };

    const handleDateChange = (field, newDate) => {
    if (field === 'start') {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
    // Validation will be handled by useEffect
  };

      const handleSave = () => {
    const validationErrors = validateDates();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    // NEW FORMAT: Save as direct calendar months (1-12) for simplicity
    const startCalendarMonth = startDate.month() + 1; // 1-based calendar month
    const endCalendarMonth = endDate.month() + 1; // 1-based calendar month
    
    // Calculate actual duration from the selected dates
    const actualDuration = Math.max(1, endDate.diff(startDate, 'month') + 1);
    
    console.log('RoleTimelineModal: Saving with calendar format:', {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      startCalendarMonth,
      endCalendarMonth,
      actualDuration,
      allocation
    });
    
    // Check if dates match milestone dates (for default timeline detection)
    const milestoneStartMonth = milestoneContext?.milestoneStartMonth || 1;
    const milestoneEndMonth = milestoneContext?.milestoneEndMonth || projectMonths;
    
    // For calendar format, compare actual calendar months instead of project-relative
    const milestoneStart = dayjs(milestoneContext?.milestoneStartDate || `${new Date().getFullYear()}-01-01`);
    const milestoneEnd = dayjs(milestoneContext?.milestoneEndDate || `${new Date().getFullYear()}-12-31`);
    const milestoneStartCalendarMonth = milestoneStart.month() + 1;
    const milestoneEndCalendarMonth = milestoneEnd.month() + 1;
    
    const isDefaultTimeline = startCalendarMonth === milestoneStartCalendarMonth && endCalendarMonth === milestoneEndCalendarMonth;
    
    const updatedRole = {
      ...role,
      allocation: allocation,
      useDefaultTimeline: isDefaultTimeline,
      defaultAllocation: isDefaultTimeline ? allocation : (role.defaultAllocation || 100),
      overlap_months: actualDuration, // Use actual duration from selected dates
      dateRanges: [{
        startMonth: startCalendarMonth, // Direct calendar month (1-12)
        endMonth: endCalendarMonth, // Direct calendar month (1-12)
        allocation: allocation,
        timelineVersion: '3.0', // Mark as new calendar format
        lastTimelineUpdate: new Date().toISOString() // Track when this was edited
      }],
      // Update milestone boundary fields for consistency (keep for backwards compatibility)
      milestone_start_month: milestoneStartMonth,
      milestone_end_month: milestoneEndMonth,
      timelineVersion: '3.0', // Mark role as using new format
      lastTimelineUpdate: new Date().toISOString()
    };
    
    console.log('RoleTimelineModal: Saving role with calendar format:', {
      roleId: role.id,
      roleName: role.name,
      allocation: allocation,
      startCalendarMonth,
      endCalendarMonth,
      isDefaultTimeline,
      timelineVersion: '3.0'
    });

    onSave(updatedRole);
    onClose();
  };

        const getTimelineSummary = () => {
    if (!startDate || !endDate) return 'Select dates and allocation';
    
    // Calculate actual months from selected dates
    const actualMonths = Math.max(1, endDate.diff(startDate, 'month') + 1);
    const effectiveMonths = (actualMonths * allocation / 100).toFixed(1);
    
    return `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')} • ${allocation}% • ${actualMonths}mo duration • ${effectiveMonths} effective months`;
  };

  const getMilestoneDateRange = () => {
    if (!milestoneContext) return null;
    
    const projectStart = dayjs(projectStartMonth + '-01');
    const milestoneStart = projectStart.add((milestoneContext.milestoneStartMonth || 1) - 1, 'month');
    const milestoneEnd = projectStart.add((milestoneContext.milestoneEndMonth || projectMonths) - 1, 'month');
    
    return {
      start: milestoneStart,
      end: milestoneEnd,
      display: `${milestoneStart.format('MMM YYYY')} - ${milestoneEnd.format('MMM YYYY')}`
    };
  };

    const calculateCost = () => {
    if (!startDate || !endDate || !role?.rate) return 0;
    
    // Calculate actual months from selected dates
    const actualMonths = Math.max(1, endDate.diff(startDate, 'month') + 1);
    const monthlyCost = (role.rate * allocation / 100);
    return monthlyCost * actualMonths;
  };

    if (!role) return null;

  const milestoneRange = getMilestoneDateRange();
  const estimatedCost = calculateCost();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center'
            }}>
              <PersonIcon sx={{ fontSize: '1.2rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {role.name} Timeline
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {milestoneContext?.milestoneName || 'Role Timeline'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
                <DialogContent sx={{ pt: 2 }}>
          {/* Date Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" sx={{ fontSize: '1rem' }} />
              Role Timeline
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newDate) => handleDateChange('start', newDate)}
                disabled={readOnly}
                views={['year', 'month']}
                openTo="month"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.dates,
                    helperText: errors.dates ? '' : 'Select any month'
                  }
                }}
              />
                            <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newDate) => handleDateChange('end', newDate)}
                disabled={readOnly}
                views={['year', 'month']}
                openTo="month"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.dates,
                    helperText: errors.dates ? '' : 'Select any month'
                  }
                }}
              />
            </Stack>
            
            {errors.dates && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                {errors.dates}
              </Typography>
            )}
          </Box>

                    {/* Allocation Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" sx={{ fontSize: '1rem' }} />
              Allocation Percentage
            </Typography>
            
            {/* Preset Allocation Buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[10, 20, 25, 50, 75, 100].map((preset) => (
                <Chip
                  key={preset}
                  label={`${preset}%`}
                  onClick={() => !readOnly && setAllocation(preset)}
                  color={allocation === preset ? 'primary' : 'default'}
                  variant={allocation === preset ? 'filled' : 'outlined'}
                  clickable={!readOnly}
                  sx={{
                    minWidth: 60,
                    fontWeight: allocation === preset ? 600 : 400,
                    cursor: readOnly ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor: readOnly ? 'transparent' : (allocation === preset ? 'primary.dark' : 'action.hover')
                    }
                  }}
                  disabled={readOnly}
                />
              ))}
            </Box>
            
                        {/* Custom Allocation Input */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Custom:
              </Typography>
              <TextField
                label="Allocation %"
                type="number"
                value={allocation}
                                onChange={(e) => {
                  const inputValue = e.target.value;
                  // Allow empty input for better UX
                  if (inputValue === '') {
                    setAllocation(0);
                  } else {
                    const numValue = Number(inputValue);
                    // Allow any number input, validation happens in real-time
                    if (!isNaN(numValue)) {
                      setAllocation(Math.max(0, Math.min(100, numValue)));
                    }
                  }
                  // Validation will be handled by useEffect
                }}
                onBlur={(e) => {
                  // Ensure valid range on blur
                  const value = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                  setAllocation(value);
                }}
                size="small"
                sx={{ width: 120 }}
                inputProps={{ 
                  min: 0, 
                  max: 100,
                  step: 0.1 // Allow decimal values
                }}
                disabled={readOnly}
                error={!!errors.allocation}
                helperText={errors.allocation || "Enter 0-100"}
              />
              <Typography variant="body2" color="text.secondary">
                Current: {allocation}%
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

                    {/* Summary Card */}
          <Card variant="outlined" sx={{ bgcolor: 'primary.light' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 1 }}>
                Timeline Summary
              </Typography>
              <Typography variant="body2" color="primary.dark">
                {getTimelineSummary()}
              </Typography>
              {role?.rate && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`Estimated Cost: $${Math.round(estimatedCost).toLocaleString()}`}
                    size="small"
                    color="primary"
                    variant="filled"
                  />
                  <Typography variant="caption" color="primary.dark">
                    @ ${Math.round(role.rate).toLocaleString()}/month
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
                <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={readOnly || !isValid}
          sx={{ minWidth: 120 }}
        >
          Save Timeline
        </Button>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
}

export default RoleTimelineModal;